import axios from './axiosConfig'
import type { ApiRequestOptions } from './requestOptions'
import type { FinancialPaymentMethod } from './financialAccounts'

export interface ExpenseCategoryData {
 name: string
 description?: string
 branch_id?: string
 expense_account_id?: number
 is_active?: boolean
 is_labor?: boolean
}

export interface ExpenseData {
 client_uuid?: string
 expense_category_id: number
 branch_id?: string
 vault_id?: string
 cash_source?: 'drawer' | 'vault'
 payment_lines?: Array<{
  financial_account_id: string
  amount: string | number
  paid_at?: string
  idempotency_key?: string
  reference?: string
 }>
 status?: 'draft' | 'pending' | 'approved' | 'partially_paid' | 'paid' | 'cancelled'
 amount: number
 description?: string
 expense_date: string
 reference_number?: string
 notes?: string
 attachments?: File[]
}

export type ExpensePaymentPayload = {
 financial_account_id: string
 amount: string | number
 paid_at: string
 reference?: string
 idempotency_key: string
}

export interface RecurringExpenseData {
 branch_id: string
 vault_id?: string
 financial_account_id?: string
 cash_source?: 'drawer' | 'vault'
 expense_category_id: number
 title: string
 description?: string
 amount: number
 frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly'
 start_date: string
 end_date?: string
 payment_method?: FinancialPaymentMethod
 reference_number?: string
 notes?: string
}

export const expensesAPI = {
 getAll: async (params?: any, options?: ApiRequestOptions) => {
 const response = await axios.get('/expenses', { params, ...options })
 return response.data
 },

 getById: async (id: number, options?: ApiRequestOptions) => {
 const response = await axios.get(`/expenses/${id}`, options)
 return response.data
 },

 pay: async (id: number, data: ExpensePaymentPayload) => {
 const response = await axios.post(`/expenses/${id}/payments`, data)
 return response.data
 },

 create: async (data: FormData | ExpenseData) => {
 if (data instanceof FormData) {
 const response = await axios.post('/expenses', data, {
 headers: { 'Content-Type': 'multipart/form-data' },
 })
 return response.data
 }
 const response = await axios.post('/expenses', data)
 return response.data
 },

 cancel: async (id: number) => {
 const response = await axios.post(`/expenses/${id}/cancel`)
 return response.data
 },

 getCategories: async (params?: any) => {
 const response = await axios.get('/expense-categories', { params })
 return response.data
 },

 createCategory: async (data: ExpenseCategoryData) => {
 const response = await axios.post('/expense-categories', data)
 return response.data
 },

 updateCategory: async (id: number, data: ExpenseCategoryData) => {
 const response = await axios.put(`/expense-categories/${id}`, data)
 return response.data
 },

 deleteCategory: async (id: number) => {
 const response = await axios.delete(`/expense-categories/${id}`)
 return response.data
 },

 getRecurring: async (params?: any) => {
 const response = await axios.get('/recurring-expenses', { params })
 return response.data
 },

 createRecurring: async (data: RecurringExpenseData) => {
 const response = await axios.post('/recurring-expenses', data)
 return response.data
 },

 updateRecurring: async (id: string, data: Partial<RecurringExpenseData>) => {
 const response = await axios.put(`/recurring-expenses/${id}`, data)
 return response.data
 },

 deleteRecurring: async (id: string) => {
 const response = await axios.delete(`/recurring-expenses/${id}`)
 return response.data
 },
}
